import { nodeBoundingRadius, resolveNodeAppearance } from './nodeVisualGeometry';
import type { ProjectNode, ProjectRoute, ProjectSettings } from '../types/project';

// ------------------------------------------------------------
// Route polyline geometry (display + simulation sprites)
// ------------------------------------------------------------

/** Perpendicular spacing between routes that share the same endpoints. */
export const ROUTE_OVERLAP_OFFSET_PX = 8;

export function routeGeometryKey(route: ProjectRoute): string {
  const wps = route.waypoints ?? [];
  const wpKey = wps.map((w) => `${w.x},${w.y}`).join(';');
  return `${route.from_node_id}|${route.to_node_id}|${wpKey}`;
}

export function nodeBaseRadius(
  node: ProjectNode,
  settings?: ProjectSettings,
): number {
  return resolveNodeAppearance(node, settings).radius;
}

/** Node hit radius in layout coordinates (accounts for display scale counter-zoom). */
export function nodeEffectiveRadius(
  node: ProjectNode,
  nodeDisplayScale: number,
  zoom: number,
  settings?: ProjectSettings,
): number {
  const appearance = resolveNodeAppearance(node, settings);
  const z = zoom > 0 ? zoom : 1;
  return (
    nodeBoundingRadius(appearance.shape, appearance.radius) *
    (nodeDisplayScale / z)
  );
}

/** Point on circle boundary at (cx,cy) in direction toward (tx,ty). */
export function pointOnCircleToward(
  cx: number,
  cy: number,
  radius: number,
  tx: number,
  ty: number,
): { x: number; y: number } {
  const dx = tx - cx;
  const dy = ty - cy;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) {
    return { x: cx + radius, y: cy };
  }
  return {
    x: cx + (dx / len) * radius,
    y: cy + (dy / len) * radius,
  };
}

export function buildRoutePoints(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
): number[] {
  const from = nodesById.get(route.from_node_id);
  const to = nodesById.get(route.to_node_id);
  if (!from || !to) return [];

  const points: number[] = [from.x, from.y];
  for (const wp of route.waypoints ?? []) {
    points.push(wp.x, wp.y);
  }
  points.push(to.x, to.y);
  return points;
}

/** Trim polyline endpoints to node circle edges (ports). */
export function trimRoutePointsToNodeEdges(
  points: number[],
  from: ProjectNode,
  to: ProjectNode,
  nodeDisplayScale: number,
  zoom: number,
  settings?: ProjectSettings,
): number[] {
  if (points.length < 4) return points;

  const fromR = nodeEffectiveRadius(from, nodeDisplayScale, zoom, settings);
  const toR = nodeEffectiveRadius(to, nodeDisplayScale, zoom, settings);

  const start = pointOnCircleToward(
    from.x,
    from.y,
    fromR,
    points[2],
    points[3],
  );
  const end = pointOnCircleToward(
    to.x,
    to.y,
    toR,
    points[points.length - 4],
    points[points.length - 3],
  );

  return [
    start.x,
    start.y,
    ...points.slice(2, points.length - 2),
    end.x,
    end.y,
  ];
}

export function buildConnectedRoutePoints(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  displayOffsetPx: number,
  nodeDisplayScale: number,
  zoom: number,
  settings?: ProjectSettings,
): number[] {
  const from = nodesById.get(route.from_node_id);
  const to = nodesById.get(route.to_node_id);
  if (!from || !to) return [];

  const centerline = buildRoutePoints(route, nodesById);
  if (centerline.length < 4) return centerline;

  const trimmed = trimRoutePointsToNodeEdges(
    centerline,
    from,
    to,
    nodeDisplayScale,
    zoom,
    settings,
  );
  return offsetPolyline(trimmed, displayOffsetPx);
}

/** Port positions for selected-route indicators. */
export function getRoutePortPositions(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  nodeDisplayScale: number,
  zoom: number,
  settings?: ProjectSettings,
): { from: { x: number; y: number }; to: { x: number; y: number } } | null {
  const from = nodesById.get(route.from_node_id);
  const to = nodesById.get(route.to_node_id);
  if (!from || !to) return null;

  const centerline = buildRoutePoints(route, nodesById);
  if (centerline.length < 4) return null;

  const trimmed = trimRoutePointsToNodeEdges(
    centerline,
    from,
    to,
    nodeDisplayScale,
    zoom,
    settings,
  );
  return {
    from: { x: trimmed[0], y: trimmed[1] },
    to: {
      x: trimmed[trimmed.length - 2],
      y: trimmed[trimmed.length - 1],
    },
  };
}

export function offsetPolyline(points: number[], offsetPx: number): number[] {
  if (offsetPx === 0 || points.length < 4) return points;

  const x0 = points[0];
  const y0 = points[1];
  const x1 = points[points.length - 2];
  const y1 = points[points.length - 1];
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return points;

  const nx = -dy / len;
  const ny = dx / len;
  const result: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    result.push(points[i] + nx * offsetPx, points[i + 1] + ny * offsetPx);
  }
  return result;
}

export function buildDisplayRoutePoints(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  displayOffsetPx: number,
  nodeDisplayScale = 1,
  zoom = 1,
  settings?: ProjectSettings,
): number[] {
  return buildConnectedRoutePoints(
    route,
    nodesById,
    displayOffsetPx,
    nodeDisplayScale,
    zoom,
    settings,
  );
}

/** Sum of segment lengths along a flat [x,y,…] polyline in layout pixels. */
export function polylineLengthPx(points: number[]): number {
  let total = 0;
  for (let i = 0; i < points.length - 2; i += 2) {
    total += Math.hypot(points[i + 2] - points[i], points[i + 3] - points[i + 1]);
  }
  return total;
}

/** Real-world route length from layout geometry and calibration scale. */
export function computeRouteLengthMeters(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  pixelsPerMeter: number,
): number | null {
  if (pixelsPerMeter <= 0) return null;
  const points = buildRoutePoints(route, nodesById);
  if (points.length < 4) return 0;
  return polylineLengthPx(points) / pixelsPerMeter;
}

/** Persist layout-derived route lengths for simulation travel time. */
export function syncRouteLengthsInDocument<
  T extends {
    nodes: ProjectNode[];
    routes: ProjectRoute[];
    settings?: { pixels_per_meter?: number };
  },
>(document: T): T {
  const pixelsPerMeter = document.settings?.pixels_per_meter ?? 0;
  if (pixelsPerMeter <= 0) return document;
  const nodesById = new Map(document.nodes.map((node) => [node.id, node]));
  const routes = document.routes.map((route) => {
    const length = computeRouteLengthMeters(route, nodesById, pixelsPerMeter);
    if (length === null) return route;
    const nextLength = Math.round(length * 1000) / 1000;
    if (route.length_meters === nextLength) return route;
    return {
      ...route,
      length_meters: nextLength,
    };
  });
  const changed = routes.some((route, index) => route !== document.routes[index]);
  if (!changed) return document;
  return { ...document, routes };
}

/** Display-only perpendicular offsets for routes that share identical geometry. */
export function computeRouteDisplayOffsets(
  routes: ProjectRoute[],
): Map<string, number> {
  const groups = new Map<string, ProjectRoute[]>();
  for (const route of routes) {
    const key = routeGeometryKey(route);
    const list = groups.get(key) ?? [];
    list.push(route);
    groups.set(key, list);
  }

  const offsets = new Map<string, number>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => {
      const ai = a.output_index ?? 0;
      const bi = b.output_index ?? 0;
      if (ai !== bi) return ai - bi;
      return a.id.localeCompare(b.id);
    });
    const n = sorted.length;
    sorted.forEach((route, index) => {
      const offset = (index - (n - 1) / 2) * ROUTE_OVERLAP_OFFSET_PX;
      offsets.set(route.id, offset);
    });
  }
  return offsets;
}

export interface OverlapGroup {
  routeIds: string[];
  routeNames: string[];
}

export function findOutgoingOverlapGroups(
  routes: ProjectRoute[],
  nodeId: string,
): OverlapGroup[] {
  const outgoing = routes.filter((r) => r.from_node_id === nodeId);
  const groups = new Map<string, ProjectRoute[]>();
  for (const route of outgoing) {
    const key = routeGeometryKey(route);
    const list = groups.get(key) ?? [];
    list.push(route);
    groups.set(key, list);
  }

  return Array.from(groups.values())
    .filter((group) => group.length > 1)
    .map((group) => {
      const sorted = [...group].sort((a, b) => a.name.localeCompare(b.name));
      return {
        routeIds: sorted.map((r) => r.id),
        routeNames: sorted.map((r) => r.name),
      };
    });
}

/**
 * Map engine progress (0–1 along center-to-center route length) to display
 * polyline parameter (0–1 along trimmed port-to-port path).
 */
export function simProgressToDisplayPolylineT(
  progress: number,
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  nodeDisplayScale: number,
  zoom: number,
  pixelsPerMeter = 0,
): number {
  const clamped = Math.min(1, Math.max(0, progress));
  const from = nodesById.get(route.from_node_id);
  const to = nodesById.get(route.to_node_id);
  if (!from || !to) return clamped;

  const centerline = buildRoutePoints(route, nodesById);
  if (centerline.length < 4) return clamped;

  const centerLength = polylineLengthPx(centerline);
  const trimmed = trimRoutePointsToNodeEdges(
    centerline,
    from,
    to,
    nodeDisplayScale,
    zoom,
  );
  const trimLength = polylineLengthPx(trimmed);
  if (centerLength <= 0 || trimLength <= 0) return clamped;

  const fromR = nodeEffectiveRadius(from, nodeDisplayScale, zoom);
  const configuredLengthPx =
    route.length_meters != null &&
    route.length_meters > 0 &&
    pixelsPerMeter > 0
      ? route.length_meters * pixelsPerMeter
      : centerLength;
  const simDistance = clamped * configuredLengthPx;
  const displayDistance = simDistance - fromR;

  if (displayDistance <= 0) return 0;
  if (displayDistance >= trimLength) return 1;
  return displayDistance / trimLength;
}

const routePointsCache = new Map<string, number[]>();

export function buildRouteGeometryCacheKey(
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  displayOffsetPx: number,
  nodeDisplayScale: number,
  zoom: number,
): string {
  const from = nodesById.get(route.from_node_id);
  const to = nodesById.get(route.to_node_id);
  const wpKey = (route.waypoints ?? []).map((w) => `${w.x},${w.y}`).join(';');
  return [
    route.id,
    from?.x ?? 0,
    from?.y ?? 0,
    to?.x ?? 0,
    to?.y ?? 0,
    wpKey,
    displayOffsetPx,
    nodeDisplayScale,
    zoom,
  ].join('|');
}

export function getCachedRoutePoints(
  key: string,
  route: ProjectRoute,
  nodesById: Map<string, ProjectNode>,
  displayOffsetPx: number,
  nodeDisplayScale: number,
  zoom: number,
): number[] {
  const cached = routePointsCache.get(key);
  if (cached) return cached;
  const points = buildDisplayRoutePoints(
    route,
    nodesById,
    displayOffsetPx,
    nodeDisplayScale,
    zoom,
  );
  routePointsCache.set(key, points);
  if (routePointsCache.size > 512) {
    const firstKey = routePointsCache.keys().next().value;
    if (firstKey) routePointsCache.delete(firstKey);
  }
  return points;
}

export function pointAlongPolyline(
  points: number[],
  t: number,
): { x: number; y: number } {
  if (points.length < 2) {
    return { x: 0, y: 0 };
  }
  if (points.length === 2) {
    return { x: points[0], y: points[1] };
  }

  const clamped = Math.min(1, Math.max(0, t));
  let totalLen = 0;
  const segments: Array<{
    len: number;
    ax: number;
    ay: number;
    bx: number;
    by: number;
  }> = [];

  for (let i = 0; i < points.length - 2; i += 2) {
    const ax = points[i];
    const ay = points[i + 1];
    const bx = points[i + 2];
    const by = points[i + 3];
    const len = Math.hypot(bx - ax, by - ay);
    segments.push({ len, ax, ay, bx, by });
    totalLen += len;
  }

  if (totalLen <= 0) {
    return { x: points[0], y: points[1] };
  }

  let dist = clamped * totalLen;
  for (const seg of segments) {
    if (dist <= seg.len || seg === segments[segments.length - 1]) {
      const u = seg.len > 0 ? dist / seg.len : 0;
      return {
        x: seg.ax + u * (seg.bx - seg.ax),
        y: seg.ay + u * (seg.by - seg.ay),
      };
    }
    dist -= seg.len;
  }

  const last = segments[segments.length - 1];
  return { x: last.bx, y: last.by };
}
